import { Link, useNavigate } from 'react-router-dom';

import { Moon, Sun, LogIn, LogOut, User, Bell, Settings, Wallet } from 'lucide-react';

import { Button } from '@shared';
import { t } from '@/i18n';
import { useAuth } from '@/router/AuthContext';
import { useTheme } from '@/ThemeProvider';
import { MarketSummaryStrip } from '@/components/header/MarketSummaryStrip';
import { LanguageToggle } from '@/components/header/LanguageToggle';
import { logCustomEvent } from '@/shared/configs/firebase';

/** TRAD-3701/3702 — 헤더 «내 자산» 클릭(web 전용, 파라미터 없음 · 팀장 확정 10/12). 동의 게이트는 logCustomEvent 가 맡는다. */
export const HEADER_MY_ASSETS_CLICK_EVENT = 'header_my_assets_click';

const Header = () => {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const { isAuthenticated, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="h-12 px-4 bg-card border-b border-border w-full flex items-center justify-between shrink-0">
      {/* Left: Logo + Market Summary
          TRAD-3600 — 폭이 모자라면 «왼쪽(시세 띠)» 이 줄어든다(min-w-0). 로고와 오른쪽 버튼 묶음은 줄어들지 않는다(shrink-0).
          종전엔 양쪽이 같이 줄어 768~816px 에서 «로그인» 글자가 세로로 쪼개졌다(시세 띠가 md 에서 나타나는 순간). */}
      <div className="flex min-w-0 items-center gap-6">
        {/* TRAD-3702 — 400px 미만은 «XTrading» 글자를 숨기고 아이콘만(팀장 결정 10/12 (A)). 오른쪽 묶음은 줄지 않으므로(TRAD-3600)
            «내 자산» 버튼(36+간격 8)이 들어오면서 375·360 에서 로고 글자가 언어 토글을 덮었다(실측 −10·−25px, 320 은 종전에도 −21).
            글자를 숨기면 약 70px 를 확보한다. 링크 이름은 aria-label 로 유지(아이콘 alt 는 장식 — 이름이 두 번 읽히지 않게).
            실측 재현: `node scripts/verify-trad3702-header-narrow.mjs`(dev 서버 필요 · jsdom 은 레이아웃을 못 잰다). */}
        <Link
          to="/"
          aria-label="XTrading"
          className="flex shrink-0 items-center gap-1.5 text-base font-bold text-foreground tracking-tight"
        >
          <img src="/images/playstore.png" alt="" className="w-6 h-6 rounded bg-white p-0.5" />
          <span className="hidden min-[400px]:inline">XTrading</span>
        </Link>
        <div className="hidden md:block h-4 w-px bg-border" />
        <MarketSummaryStrip />
      </div>

      {/* Right: Theme + Auth — 줄어들지도 줄바꿈되지도 않는다(TRAD-3600)
          TRAD-3702 — sm 미만은 간격 8→6(~~gap-2~~): 아이콘 7개라 12px 를 벌어 320 에서도 로고와 ≥8px(실측 6→18). sm 이상은 그대로. */}
      <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap sm:gap-2">
        <LanguageToggle />

        <Button
          variant="ghost"
          onClick={toggleDarkMode}
          className="h-auto rounded p-1.5 text-muted-foreground hover:bg-secondary"
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
        </Button>

        {isAuthenticated ? (
          <>
            {/* TRAD-3702 — «내 자산» 1클릭 진입점(davidyoon 10/12, 시안 A안 = 알림 왼쪽 보라 pill). 로그인 전용 —
                비로그인에게는 그리지 않는다(드롭다운 «내 정보 → 내 자산»·하단 «더보기 → 자산»은 그대로 남는다).
                ~~모양은 «마이» 링크와 같은 꼴(26px·12px)~~ → davidyoon 10/12 «시안 보여준 방식으로»: 시안 A안 그대로 높이 36 · 글자 14 semibold ·
                둥근 pill · 아이콘 16 · 간격 6 · 좌우 14(sm 이상). 옆 «마이/로그아웃»보다 큰 것은 의도(«내 자산을 더 눈에 띄게»).
                sm 미만은 아이콘만 36×36(탭 영역). 색은 시안 hex 가 아니라
                토큰(채움 primary/10 · 글자 brand-strong). ~~brand-text~~ 는 «card 위» 값이라 보라 채움 위 다크에서 4.36:1(AA 미달,
                14px semibold 도 «큰 글자»(18.66px bold) 미만이라 4.5 기준) — 토큰 규칙대로 «더 밝은 표면 위» 값 brand-strong 을 쓴다: 라이트 4.91 · 다크 5.04 (hover primary/15: 4.53 · 4.85).
                라벨 키는 드롭다운과 같은 `nav.portfolio`(신규 키 없음). sm 미만은 아이콘만 → aria-label 로 이름을 준다. */}
            <Link
              to="/portfolio"
              aria-label={t('nav.portfolio')}
              onClick={() => logCustomEvent(HEADER_MY_ASSETS_CLICK_EVENT)}
              className="flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 text-sm font-semibold text-brand-strong transition-colors hover:bg-primary/15 sm:w-auto sm:px-3.5"
            >
              <Wallet size={16} aria-hidden="true" />
              <span className="hidden sm:inline">{t('nav.portfolio')}</span>
            </Link>
            <Link
              to="/my-page/notifications"
              className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors"
            >
              <Bell size={16} />
            </Link>
            <Link
              to="/my-page/settings"
              className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors"
            >
              <Settings size={16} />
            </Link>
            <Link
              to="/my-page"
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-secondary text-muted-foreground text-xs transition-colors"
            >
              <User size={14} />
              <span className="hidden sm:inline">{t('header.nav.my_page')}</span>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="h-auto gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-secondary"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">{t('header.nav.logout')}</span>
            </Button>
          </>
        ) : (
          <Link
            to="/login"
            className="flex items-center gap-1 px-2.5 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
          >
            <LogIn size={14} />
            <span>{t('header.nav.login')}</span>
          </Link>
        )}
      </div>
    </header>
  );
};

export default Header;
